import { Rng } from '../rng';
import type { OutcomeTier } from './outcome';

/**
 * Die Suche am Fundort.
 *
 * Bis hierher war der Einsatzort ein Ergebnisbildschirm: Man kam an und las,
 * was herausgekommen war. Dabei ist das Wrack selten dort, wo es gemeldet
 * wurde — es treibt, sinkt schraeg, und die letzte Position stammt von einem
 * Funkspruch in Panik.
 *
 * Also wird gesucht, und zwar mit dem Werkzeug, das das Spiel ohnehin hat:
 * dem Sonar, dessen Echo *echt* gerechnet ist. Schall laeuft im Meerwasser mit
 * rund 1.500 m/s; die Zeit bis zur Antwort ist die Entfernung. Wer zuhoert,
 * kann aus drei, vier Pings das Wrack einkreisen — und wer nicht hoert, sieht
 * denselben Ring auf dem Schirm.
 *
 * Nur bei Bergung und kalter Spur: Bei einer Rettung sind Menschen im Wasser,
 * die gesehen und gehoert werden wollen, und da soll niemand Minispiele
 * spielen.
 *
 * Deterministisch: Wrack und Messrauschen haengen am Seed des Falls und an
 * der Kennung des Vorfalls. Ein Neuladen ergibt dieselbe Suche.
 */

/** Schallgeschwindigkeit in Meerwasser, in Metern je Sekunde. */
export const SPEED_OF_SOUND_IN_WATER = 1500;

/** Zeit bis zum Echo — hin und zurueck. */
export function echoDelaySeconds(distanceMetres: number): number {
  return (2 * Math.max(0, distanceMetres)) / SPEED_OF_SOUND_IN_WATER;
}

/** Kantenlaenge des Suchfelds: zwei Seemeilen. */
export const FIELD_METRES = 2 * 1852;

/** So nah muss der Tauchpunkt am Wrack liegen. */
export const FIND_RADIUS_METRES = 260;

/** Wie viel Spielzeit ein Ping samt Auswertung kostet, in Stunden. */
export const HOURS_PER_PING = 1 / 3;

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface PingResult {
  readonly at: Point;
  /** Gemessene Entfernung — mit Messfehler. */
  readonly rangeMetres: number;
  readonly echoSeconds: number;
  /** Wie deutlich das Echo ist, 0 bis 1. Naeher ist deutlicher. */
  readonly strength: number;
}

export interface SonarSearch {
  readonly budget: number;
  /** Groesse des Messfehlers in Metern (Halbbreite des Rings). */
  readonly uncertaintyMetres: number;
  readonly pings: readonly PingResult[];
  readonly pingsLeft: number;
  ping(at: Point): PingResult | null;
  /** Taucht an diesem Punkt. Liefert, ob das Wrack getroffen ist. */
  dive(at: Point): { found: boolean; missMetres: number };
  /** Nur fuer Tests und die Aufloesung nach dem Tauchgang. */
  readonly wreck: Point;
}

export function createSonarSearch(params: {
  seed: string;
  incidentId: string;
  tier: OutcomeTier;
  submarine: boolean;
  seaState: number;
  /** Zusaetzliche Pings aus dem Schwierigkeitsgrad, auch negativ. */
  extraPings?: number;
}): SonarSearch {
  const rng = Rng.fromSeed(params.seed).derive(`site:${params.incidentId}`);
  const half = FIELD_METRES / 2;

  // Je kaelter die Spur und je rauer die See, desto weiter ist es getrieben.
  const drift = (params.tier === 'cold' ? 0.75 : 0.5) * (1 + params.seaState / 18);
  const angle = rng.float(0, Math.PI * 2);
  const distance = half * Math.min(0.85, drift * Math.sqrt(rng.next()));
  const wreck: Point = {
    x: half + Math.cos(angle) * distance,
    y: half + Math.sin(angle) * distance,
  };

  /*
   * Der Messfehler ist beschraenkt, nicht normalverteilt.
   *
   * Ein gleichverteilter Fehler mit fester Obergrenze garantiert, dass sauberes
   * Einkreisen immer zum Ziel fuehrt — ein Ausreisser, der eine gute Suche
   * zunichtemacht, fuehlte sich wie Betrug an. Das U-Boot misst getaucht
   * ruhiger als das Schiff an der Oberflaeche.
   */
  const uncertaintyMetres = (params.submarine ? 30 : 45) + params.seaState * 6;
  // Nie unter drei: Mit zwei Ringen bleiben zwei Schnittpunkte, und Raten
  // waere keine Suche mehr.
  const budget = Math.max(3, (params.submarine ? 6 : 4) + (params.extraPings ?? 0));

  const noise = rng.derive('noise');
  const pings: PingResult[] = [];

  const clampToField = (p: Point): Point => ({
    x: Math.max(0, Math.min(FIELD_METRES, p.x)),
    y: Math.max(0, Math.min(FIELD_METRES, p.y)),
  });

  return {
    budget,
    uncertaintyMetres,
    get pings() {
      return pings;
    },
    get pingsLeft() {
      return budget - pings.length;
    },
    wreck,
    ping(at) {
      if (pings.length >= budget) return null;
      const point = clampToField(at);
      const truth = Math.hypot(point.x - wreck.x, point.y - wreck.y);
      const measured = Math.max(0, truth + noise.float(-uncertaintyMetres, uncertaintyMetres));
      const result: PingResult = {
        at: point,
        rangeMetres: measured,
        echoSeconds: echoDelaySeconds(measured),
        strength: Math.max(0.1, 1 - measured / (FIELD_METRES * 1.5)),
      };
      pings.push(result);
      return result;
    },
    dive(at) {
      const point = clampToField(at);
      const missMetres = Math.hypot(point.x - wreck.x, point.y - wreck.y);
      return { found: missMetres <= FIND_RADIUS_METRES, missMetres };
    },
  };
}

/**
 * Der Punkt, der am besten zu allen Messungen passt.
 *
 * Eine grobe Rastersuche mit anschliessender Verfeinerung — fuer vier Kreise
 * genuegt das, und es ist nachvollziehbar. Gebraucht vom Test, der beweist,
 * dass die Suche immer loesbar ist. Bewusst nicht in der Anzeige: Das
 * Einkreisen ist die Aufgabe.
 */
export function bestEstimate(pings: readonly PingResult[]): Point | null {
  if (pings.length < 3) return null;

  const residual = (p: Point): number =>
    pings.reduce((sum, ping) => {
      const d = Math.hypot(p.x - ping.at.x, p.y - ping.at.y) - ping.rangeMetres;
      return sum + d * d;
    }, 0);

  let best: Point = { x: FIELD_METRES / 2, y: FIELD_METRES / 2 };
  let bestScore = residual(best);
  let step = FIELD_METRES / 20;

  for (let x = 0; x <= FIELD_METRES; x += step) {
    for (let y = 0; y <= FIELD_METRES; y += step) {
      const score = residual({ x, y });
      if (score < bestScore) {
        bestScore = score;
        best = { x, y };
      }
    }
  }

  // Verfeinern um den besten Rasterpunkt.
  while (step > 2) {
    step /= 2;
    let improved = true;
    while (improved) {
      improved = false;
      for (const [dx, dy] of [[step, 0], [-step, 0], [0, step], [0, -step]] as const) {
        const candidate = { x: best.x + dx, y: best.y + dy };
        const score = residual(candidate);
        if (score < bestScore) {
          bestScore = score;
          best = candidate;
          improved = true;
        }
      }
    }
  }

  return best;
}
